// One match a map is narrowed to (Ben, 2026-10-18: "the same information as Catalyst, but better" —
// link the modules; and a drag, not a click, so nothing changes that the user didn't mean). Drag a
// match's minutes bar from Matches onto a Shot Map, Pass Map, Heat Map or Passing Profile showing the
// same player: that module alone shows only that game, with a chip whose ✕ goes back to the season.
// Kept on the module (`_eventMatchFocus`), so Undo and saved dashboards keep it.

import type { MatchRef } from "./match.js";
import { getModState, setModState } from "../modules/bridge.js";
import type { PlayerId } from "../data/players.js";
import { PITCH_L, PITCH_W } from "../engines/event-map-kit.js";

/** What a map is narrowed to, dragged in from Matches (Ben, 2026-10-18): one match (a minutes bar),
 *  or a position across the season (a "minutes by position" chip). Each match it covers, and — for
 *  a position — only her minutes there (`windows`, on the match clock: "only passes he made in the
 *  period when he was playing right back"). `label` = the chip's words. */
export interface EventFocus {
  kind: "match" | "position" | "season" | "zone" | "passes";
  playerId: PlayerId;
  /** The league + season the matches are in: a map switched to this player opens on them. */
  competitionId: number;
  seasonCode: string;
  label: string;
  matches: { matchId: number; windows?: { from: number; to: number }[] }[];
  /** A "zone" focus (Ben, 2026-10-19 — zones dragged from a Pass Map onto a Heat Map): only events
   *  starting in these cells of the 6 × 6 zone grid (`zoneOf`). Its `matches` is the Pass Map's own
   *  match / position focus when it had one, else empty = the whole season. */
  zones?: number[];
  /** A "passes" focus (Ben, 2026-10-20 — a bar segment / band dragged from the Passing Report): only
   *  these events (by id). Its `matches` is empty: the ids already carry any narrowing. */
  ids?: string[];
  /** A "match" focus from a player's Matches (Ben, 2026-10-30): the match itself, the side the
   *  player played on and their name. A Shot Map or Pass Map then shows the match, a team at a time,
   *  with the player's chip on; the other maps narrow to the player's part, as before. */
  match?: MatchRef;
  playerSide?: "home" | "away";
  playerName?: string;
}

/** The Pass Map's zone grid: 6 × 6 over the pitch, as Catalyst's default (GRID_6X6). */
export const ZONE_COLS = 6;
export const ZONE_ROWS = 6;
export const ZONE_W = PITCH_L / ZONE_COLS;
export const ZONE_H = PITCH_W / ZONE_ROWS;

/** The zone a pitch point is in: column (along the pitch's length) + row × 6. Pure. */
export function zoneOf(x: number, y: number): number {
  const col = Math.min(ZONE_COLS - 1, Math.max(0, Math.floor(x / ZONE_W)));
  const row = Math.min(ZONE_ROWS - 1, Math.max(0, Math.floor(y / ZONE_H)));
  return row * ZONE_COLS + col;
}

const KEY = "_eventMatchFocus";

/** The modules a match can be dropped on (their titles). */
export const MATCH_DROP_TITLES: ReadonlySet<string> = new Set(["Shot Map", "Pass Map", "Heat Map", "Passing Profile", "Passing Report"]);

/** The match this module is narrowed to — only while it still shows that player. */
export function moduleFocus(mod: HTMLElement, playerId: PlayerId | null): EventFocus | null {
  const f = getModState<EventFocus>(mod, KEY) ?? null;
  return f && f.playerId === playerId ? f : null;
}

export function setModuleFocus(mod: HTMLElement, f: EventFocus | null): void {
  setModState(mod, KEY, f ?? undefined);
}

/** Is an event inside the focus? No focus → every event is. Otherwise it must be in one of its
 *  matches and, where that match has `windows`, in one of them (minute + second on the continuous
 *  match clock, as the Matches bars are drawn). Pure. */
export function inFocus(
  e: { id?: string; match_id: number; minute?: number; second?: number; x?: number; y?: number },
  f: EventFocus | null,
): boolean {
  if (f === null) return true;
  if (f.ids) return e.id !== undefined && f.ids.includes(e.id);
  if (f.zones) {
    if (e.x === undefined || e.y === undefined || !f.zones.includes(zoneOf(e.x, e.y))) return false;
    if (!f.matches.length) return true;
  }
  const m = f.matches.find((x) => x.matchId === e.match_id);
  if (!m) return false;
  if (!m.windows) return true;
  const t = (e.minute ?? 0) + (e.second ?? 0) / 60;
  return m.windows.some((w) => t >= w.from && t <= w.to);
}

/** A map narrowed to something with nothing of the player's in it (Ben, 2026-10-30: "No shots in
 *  that match" read as the match having none): whose, what and where. "Declan Rice had no shots in
 *  that match (Sunderland 0–2 Arsenal)." Pure. */
export function focusEmptyText(shortName: string, pluralNoun: string, focus: EventFocus): string {
  const where =
    focus.kind === "position"
      ? "at that position"
      : focus.kind === "zone"
        ? "from those zones"
        : focus.kind === "passes"
          ? "in that set"
          : focus.kind === "season"
            ? "that season"
            : `in that match (${focus.label})`;
  return `${shortName || "This player"} had no ${pluralNoun} ${where}. ✕ the chip for the whole season.`;
}
